import { CancelOutlined, EditOutlined } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
	Card,
	CardContent,
	CardHeader,
	Grid,
	IconButton,
	Link,
	Stack,
	Typography,
} from '@mui/material';
import LegacySystemList from 'components/pages/site/legacy-system';
import { CheckboxField, TextField } from 'components/ui/fields';
import { PageContent, PageTitle } from 'components/ui/page';
import { Formik, FormikProps } from 'formik';
import { EvolveSettings, EvolveSettingsUpdate } from 'middleware-types';
import { useEvolveSettings, useUpdateEvolveSettings } from 'pages/app/settings';
import { useState } from 'react';
import { useIsSiteAdmin } from 'utils/useSiteUser';
import { useValidation } from 'utils/useValidation';
import { useStressTestCreateMutation } from './hooks';

/**
 * Currently this is the homepage.
 */
export const SiteUserDashboard: React.FC = () => {
	const { evolveSettings } = useEvolveSettings();
	const env = import.meta.env;
	const fullUrl = window.location.href;

	const showTestingTools =
		fullUrl.startsWith('http://localhost') ||
		fullUrl.startsWith('https://test1') ||
		fullUrl.startsWith('https://test2');

	const { createStressTest, loading } = useStressTestCreateMutation();

	return (
		<>
			<PageTitle title="Dashboard" />
			<PageContent>
				<Stack
					gap={2}
					flexWrap="wrap"
					sx={{
						flexDirection: { xs: 'column', sm: 'row' },
						justifyContent: 'space-between',
					}}>
					<Stack gap={2} sx={{ flexDirection: 'column' }}>
						{showTestingTools && (
							<Card className="w-96">
								<CardHeader title="Testing Tools/Links" />
								<CardContent>
									<Stack spacing={1} gap={1}>
										<Link
											href={`https://evolution-global.sentry.io/issues/?environment=${env.VITE_SENTRY_ENVIRONMENT}`}
											target="_blank">
											Sentry
										</Link>
										<Link
											href={`https://us-east-1.console.aws.amazon.com/cognito/v2/idp/user-pools/${env.VITE_AWS_COGNITO_USERPOOLID}/users?region=${env.VITE_AWS_REGION}`}
											target="_blank">
											Cognito
										</Link>
										<Link href="/site/dev/theme">Theme</Link>
										<LoadingButton
											variant="contained"
											color="warning"
											loading={loading}
											onClick={createStressTest}>
											Create Stress Test Data
										</LoadingButton>
									</Stack>
								</CardContent>
							</Card>
						)}
						{evolveSettings && <EvolveSettingsForm evolveSettings={evolveSettings} />}
					</Stack>
					<LegacySystemList />
				</Stack>
			</PageContent>
		</>
	);
};

const EvolveSettingsForm = ({ evolveSettings }: { evolveSettings: EvolveSettings }) => {
	const { updateEvolveSettings } = useUpdateEvolveSettings();
	const initialValues: EvolveSettings = {
		...evolveSettings,
	};

	const validation = useValidation('EvolveSettingsUpdate');

	const [isEditing, setIsEditing] = useState<boolean>(false);
	const onSubmit = async (update: EvolveSettingsUpdate) => {
		updateEvolveSettings(evolveSettings.id, update).then((res) => {
			if (res) {
				setIsEditing(false);
			}
		});
	};

	const { isSiteAdmin } = useIsSiteAdmin();

	return (
		<Formik<EvolveSettingsUpdate>
			initialValues={initialValues}
			onSubmit={onSubmit}
			validationSchema={validation.schema}>
			{({ isSubmitting, isValid, submitForm }: FormikProps<EvolveSettingsUpdate>) => (
				<Card className="w-96">
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						pr={1}>
						<CardHeader title="Evolve Settings" />
						{!isSiteAdmin ? (
							<></>
						) : isEditing ? (
							<IconButton onClick={() => setIsEditing(false)}>
								<CancelOutlined />
							</IconButton>
						) : (
							<IconButton onClick={() => setIsEditing(true)}>
								<EditOutlined />
							</IconButton>
						)}
					</Stack>
					<Stack p={2.5}>
						{isEditing ? (
							<EvolveSettingsFields />
						) : (
							<EvolveSettingsRead evolveSettings={evolveSettings} />
						)}
					</Stack>
					{isEditing && (
						<Stack direction="row" justifyContent="flex-end" padding={2}>
							<LoadingButton
								variant="contained"
								color="primary"
								loading={isSubmitting}
								disabled={!isValid}
								onClick={submitForm}>
								Save
							</LoadingButton>
						</Stack>
					)}
				</Card>
			)}
		</Formik>
	);
};

const EvolveSettingsRead = ({ evolveSettings }: { evolveSettings: EvolveSettings }) => {
	return (
		<Stack spacing={2} justifyContent="flex-start">
			<Grid container>
				<Grid item sm={10} xs={10}>
					<Typography variant="h5">Maintenance Mode:</Typography>
					<Typography variant="h5">Legacy User Interface</Typography>
				</Grid>
				<Grid
					item
					sx={{
						color: evolveSettings.isLegacyUiInMaintenanceMode ? 'warning.700' : 'auto',
						fontWeight: evolveSettings.isLegacyUiInMaintenanceMode ? 'bold' : 'auto',
						display: 'flex',
						alignItems: 'center',
					}}>
					{evolveSettings.isLegacyUiInMaintenanceMode ? 'On' : 'Off'}
				</Grid>
			</Grid>
			<Grid container>
				<Grid item sm={10} xs={10}>
					<Typography variant="h5">Maintenance Mode Message:</Typography>
				</Grid>
				<Grid
					item
					sx={{
						color: evolveSettings.isLegacyUiInMaintenanceMode ? 'warning.700' : 'auto',
						fontWeight: evolveSettings.isLegacyUiInMaintenanceMode ? 'bold' : 'auto',
						display: 'flex',
						alignItems: 'center',
					}}>
					{evolveSettings.legacyUiInMaintenanceModeMessage}
				</Grid>
			</Grid>
		</Stack>
	);
};

const EvolveSettingsFields = () => {
	return (
		<Stack alignItems="flex-start" spacing={2}>
			<Grid container alignItems="center">
				<Grid item sm={10} xs={10}>
					<Typography variant="h5">Maintenance Mode:</Typography>
					<Typography variant="h5">Legacy User Interface</Typography>
				</Grid>
				<Grid item>
					<CheckboxField
						color="primary"
						name="isLegacyUiInMaintenanceMode"
						sx={{ padding: 0 }}
					/>
				</Grid>
			</Grid>
			<Grid container alignItems="flex-start" direction="column">
				<Grid item sm={10} xs={10}>
					<Typography variant="h5">Maintenance Mode Message</Typography>
				</Grid>
				<Grid item width="100%">
					<TextField
						multiline
						minRows={3}
						color="primary"
						name="legacyUiInMaintenanceModeMessage"
						fullWidth
						sx={{ padding: 0 }}
					/>
				</Grid>
			</Grid>
		</Stack>
	);
};
